import { ChartGranularity } from "../../../models/bidAnalytics";
import { formatUtcLocalShort, toNum } from "./format";

export type HourPoint = { hour: string | Date };

function bucketKey(date: Date, granularity: ChartGranularity): string {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    const h = String(date.getHours()).padStart(2, "0");
    if (granularity === "month") {
        return `${y}-${m}`;
    }
    if (granularity === "day") {
        return `${y}-${m}-${d}`;
    }
    return `${y}-${m}-${d}T${h}`;
}

function bucketDate(key: string, granularity: ChartGranularity): Date {
    if (granularity === "month") {
        const [y, m] = key.split("-").map(Number);
        return new Date(y, m - 1, 1);
    }
    if (granularity === "day") {
        const [y, m, d] = key.split("-").map(Number);
        return new Date(y, m - 1, d);
    }
    const [datePart, hourPart] = key.split("T");
    const [y, m, d] = datePart.split("-").map(Number);
    return new Date(y, m - 1, d, Number(hourPart));
}

export function formatBucketLabel(date: Date, granularity: ChartGranularity): string {
    if (granularity === "month") {
        return date.toLocaleDateString("ru-RU", { month: "short", year: "numeric" });
    }
    if (granularity === "day") {
        return date.toLocaleDateString("ru-RU", { day: "2-digit", month: "2-digit" });
    }
    return formatUtcLocalShort(date);
}

/**
 * Агрегирует почасовой ряд с бэка в день/месяц (локальное время админа).
 * sumKeys — суммируются; rateKeys пересчитываются из numerator/denominator если заданы.
 */
export function rollupSeries<T extends HourPoint>(
    points: T[],
    granularity: ChartGranularity,
    options: {
        sumKeys: (keyof T)[];
        /** rateKey = numerator / denominator после суммирования */
        rateDefs?: Array<{ rateKey: keyof T; numerator: keyof T; denominator: keyof T }>;
        /** для averageBidFillRate: взвешенное среднее по bidsCount */
        weightedAvg?: Array<{ valueKey: keyof T; weightKey: keyof T }>;
    }
): Array<T & { x: Date }> {
    if (granularity === "hour") {
        return points
            .slice()
            .sort((a, b) => new Date(a.hour).getTime() - new Date(b.hour).getTime())
            .map((p) => ({ ...p, x: new Date(p.hour) }));
    }

    const groups = new Map<string, T[]>();
    for (const p of points) {
        const local = new Date(p.hour);
        const key = bucketKey(local, granularity);
        const list = groups.get(key) ?? [];
        list.push(p);
        groups.set(key, list);
    }

    return Array.from(groups.entries())
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([key, list]) => {
            const base = { ...list[0] } as T & { x: Date };
            base.x = bucketDate(key, granularity);
            base.hour = base.x.toISOString();

            for (const k of options.sumKeys) {
                (base as any)[k] = list.reduce((acc, row) => acc + toNum(row[k] as any), 0);
            }

            if (options.weightedAvg) {
                for (const { valueKey, weightKey } of options.weightedAvg) {
                    const weightSum = list.reduce((acc, row) => acc + toNum(row[weightKey] as any), 0);
                    if (weightSum > 0) {
                        (base as any)[valueKey] =
                            list.reduce(
                                (acc, row) =>
                                    acc + toNum(row[valueKey] as any) * toNum(row[weightKey] as any),
                                0
                            ) / weightSum;
                    } else {
                        (base as any)[valueKey] = 0;
                    }
                }
            }

            if (options.rateDefs) {
                for (const { rateKey, numerator, denominator } of options.rateDefs) {
                    const den = toNum((base as any)[denominator]);
                    const num = toNum((base as any)[numerator]);
                    (base as any)[rateKey] = den > 0 ? num / den : 0;
                }
            }

            return base;
        });
}
